import {
  type NetworkElementKind,
  type SchematicNetwork,
  type SchematicStroke,
  type SchematicStrokeWidth,
} from '@ghp/domain'

/**
 * Les libellés français de l'éditeur de schéma, et **le catalogue de GHP DIGIT** rangé en
 * familles (rapport 02 de la rétro-ingénierie, 52 outils ; #318).
 *
 * Rassemblés ici parce qu'ils sont lus à deux endroits — la palette et le nom accessible des
 * formes dessinées : deux tables de traduction finiraient par désigner le même élément par
 * deux mots, dont l'un dans le dessin et l'autre dans le bouton qui l'a posé.
 */

/** Le nom de chaque élément réseau, tel que la palette et le plan le disent. */
export const NETWORK_ELEMENT_LABELS: Readonly<Record<NetworkElementKind, string>> = {
  manhole: 'Regard',
  inspectionChamber: 'Tabouret',
  trap: 'Siphon',
  septicTank: 'Fosse',
  wastewaterPipe: 'Conduite EU',
  stormwaterPipe: 'Conduite EP',
  publicNetwork: 'Réseau public',
  manholeCover: 'Tampon',
  gratedInlet: 'Regard à grille',
  floorDrain: 'Bonde de sol',
  liftingStation: 'Poste de relevage',
  downpipe: 'Gouttière',
  plug: 'Bouchon',
  trapHorizontal: 'Siphon H',
  trapVertical: 'Siphon V',
  gratedChannel: 'Caniveau à grille',
  downpipeOutfall: 'Gouttière sur sol',
}

/**
 * Les outils de dessin : ceux du prototype validé, plus la gomme, la maison et le préau de
 * GHP DIGIT. `select`, `pan` et `eraser` n'ont pas d'équivalent dans le document : ce sont des
 * gestes, ils ne posent aucune forme.
 */
export const SCHEMATIC_TOOLS = [
  'select',
  'pan',
  'freehand',
  'eraser',
  'polygon',
  'shelter',
  'line',
  'arrow',
  'rectangle',
  'ellipse',
  'text',
] as const

export type SchematicTool = (typeof SCHEMATIC_TOOLS)[number]

/** Le nom accessible de chaque outil. Il contient la légende visible, qui en est la forme courte. */
export const SCHEMATIC_TOOL_LABELS: Readonly<Record<SchematicTool, string>> = {
  select: 'Sélectionner',
  pan: 'Déplacer le plan',
  freehand: 'Crayon',
  eraser: 'Gomme',
  polygon: 'Maison',
  shelter: 'Préau',
  line: 'Trait',
  arrow: 'Flèche',
  rectangle: 'Rectangle',
  ellipse: 'Cercle',
  text: 'Étiquette libre',
}

/**
 * La légende écrite sous l'icône. ⚠️ Sur la tablette, rien ne s'affiche au survol : une icône
 * seule se devine, et la maison ou le préau ne se devinent pas.
 */
export const SCHEMATIC_TOOL_CAPTIONS: Readonly<Record<SchematicTool, string>> = {
  select: 'Sélection',
  pan: 'Déplacer',
  freehand: 'Crayon',
  eraser: 'Gomme',
  polygon: 'Maison',
  shelter: 'Préau',
  line: 'Trait',
  arrow: 'Flèche',
  rectangle: 'Rectangle',
  ellipse: 'Cercle',
  text: 'Étiquette',
}

/** Les réseaux qu'un ouvrage peut porter, dans l'ordre du sélecteur. */
export const SCHEMATIC_NETWORK_OPTIONS: readonly { value: SchematicNetwork; label: string }[] = [
  { value: 'wastewater', label: 'EU' },
  { value: 'stormwater', label: 'EP' },
  { value: 'combined', label: 'Unitaire' },
]

/**
 * Les réseaux d'un **ouvrage** : les trois, plus « Neutre » — le regard non typé de GHP DIGIT
 * (T41), qui se dessine à l'encre. Un réseau public ou une conduite en ont toujours un.
 */
export const SCHEMATIC_STRUCTURE_NETWORK_OPTIONS: readonly {
  value: SchematicNetwork | 'ink'
  label: string
}[] = [...SCHEMATIC_NETWORK_OPTIONS, { value: 'ink', label: 'Neutre' }]

/** Les réseaux d'un **caniveau à grille** : EU ou EP, la feuille de GHP ne cite pas l'unitaire. */
export const SCHEMATIC_SEPARATE_NETWORK_OPTIONS = SCHEMATIC_NETWORK_OPTIONS.filter(
  (option) => option.value !== 'combined',
)

/** Les étages d'une étiquette de pièce, du haut vers le bas — « −1 » est le sous-sol. */
export const SCHEMATIC_LEVEL_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '2', label: '2' },
  { value: '1', label: '1' },
  { value: '0', label: '0' },
  { value: '-1', label: '−1' },
]

/**
 * Un bouton du catalogue.
 *
 * - `kind` est ce qu'il pose. `pipe` se résout en conduite EU ou EP selon le réseau choisi.
 * - `typed` : il prend le réseau choisi au lieu d'exister en double (« Tampon », pas « Tampon EU »
 *   et « Tampon EP »).
 * - `text` : une étiquette, qui se pose d'un appui et porte ce texte.
 */
export interface CatalogueItem {
  id: string
  kind: NetworkElementKind | 'pipe' | 'roomLabel' | 'outletLabel'
  label: string
  typed?: boolean
  text?: string
}

export interface CatalogueFamily {
  value: 'networks' | 'structures' | 'rooms' | 'outlets'
  label: string
  /** La famille montre le sélecteur de réseau. */
  typed?: boolean
  /** La famille montre le sélecteur d'étage. */
  level?: boolean
  items: readonly CatalogueItem[]
}

const tags = (kind: 'roomLabel' | 'outletLabel', texts: readonly string[]): CatalogueItem[] =>
  texts.map((text) => ({ id: `${kind}:${text}`, kind, label: text, text }))

/**
 * ⚠️ Les étiquettes PR, Cuve, G et Récup sont reprises **verbatim** de GHP DIGIT : leur sens est
 * une question posée à GHP (point 13 de la page de confirmation, #318). « PP » et « Epp »
 * s'affichaient « Puisard » et « Epandage » dans GHP DIGIT : c'est ce qu'on pose ici. « SH » et
 * « Dég » viennent de la recette du 20/10 (Tab6), verbatim eux aussi.
 */
export const SCHEMATIC_CATALOGUE: readonly CatalogueFamily[] = [
  {
    value: 'networks',
    label: 'Réseaux',
    typed: true,
    items: [
      { id: 'publicNetwork', kind: 'publicNetwork', label: 'Réseau public', typed: true },
      { id: 'pipe', kind: 'pipe', label: 'Conduite', typed: true },
      { id: 'gratedChannel', kind: 'gratedChannel', label: 'Caniveau à grille', typed: true },
      // La gouttière est le départ du réseau EP (#581) : sa place est ici, pas dans les Ouvrages,
      // et elle ne suit pas le sélecteur.
      { id: 'downpipe', kind: 'downpipe', label: 'Gouttière' },
      { id: 'downpipeOutfall', kind: 'downpipeOutfall', label: 'Gouttière sur sol' },
    ],
  },
  {
    value: 'structures',
    label: 'Ouvrages',
    typed: true,
    items: [
      { id: 'inspectionChamber', kind: 'inspectionChamber', label: 'Tabouret', typed: true },
      { id: 'manhole', kind: 'manhole', label: 'Regard', typed: true },
      { id: 'manholeCover', kind: 'manholeCover', label: 'Tampon', typed: true },
      { id: 'gratedInlet', kind: 'gratedInlet', label: 'Regard à grille', typed: true },
      { id: 'floorDrain', kind: 'floorDrain', label: 'Bonde de sol', typed: true },
      { id: 'liftingStation', kind: 'liftingStation', label: 'Poste de relevage', typed: true },
      { id: 'plug', kind: 'plug', label: 'Bouchon' },
      { id: 'trapHorizontal', kind: 'trapHorizontal', label: 'Siphon H' },
      { id: 'trapVertical', kind: 'trapVertical', label: 'Siphon V' },
      { id: 'septicTank', kind: 'septicTank', label: 'Fosse' },
    ],
  },
  {
    value: 'rooms',
    label: 'Pièces',
    level: true,
    items: tags('roomLabel', ['sdb', 'cuis', 'wc', 'wcB', 'év', 'L', 'LV', 'LL', 'Rob', 'LM']),
  },
  {
    value: 'outlets',
    label: 'Exutoires',
    items: tags('outletLabel', [
      'G',
      'Indéterminé',
      'Bouché',
      'Récup',
      'Puisard',
      'Épandage',
      'PR',
      'Cuve',
      'SH',
      'Dég',
    ]),
  },
]

/** La couleur de trait des formes libres, dans l'ordre de la colonne « Style ». */
export const SCHEMATIC_STROKE_LABELS: Readonly<Record<SchematicStroke, string>> = {
  ink: 'Encre',
  wastewater: 'Eaux usées',
  stormwater: 'Eaux pluviales',
  alert: 'Alerte',
  combined: 'Unitaire',
}

/**
 * Les trois couleurs de trait de la colonne « Style ». L'unitaire n'y est pas : c'est un
 * réseau, qui se choisit dans la palette, pas une couleur d'annotation. L'« Alerte » non plus :
 * son rouge est celui de l'EU depuis #579.
 */
export const SCHEMATIC_STROKE_SWATCHES: readonly SchematicStroke[] = [
  'ink',
  'wastewater',
  'stormwater',
]

export const SCHEMATIC_STROKE_WIDTH_LABELS: Readonly<Record<SchematicStrokeWidth, string>> = {
  thin: 'Trait fin',
  medium: 'Trait moyen',
  thick: 'Trait épais',
}
